import React from "react";
import clsx from "clsx";
import { useTranslation } from "lib/i18n/use-translation";

import constant from "Constant";
import styles from "./form-pieces.module.scss";

export interface HeadingProps {
  as?: "h1" | "h2";
  children: React.ReactNode;
  className?: string;
}

export const Heading: React.FC<HeadingProps> = ({
  as = "h1",
  children,
  className,
}) => {
  const Tag = as;
  return <Tag className={clsx(styles["heading"], className)}>{children}</Tag>;
};

export interface SubtitleProps {
  children: React.ReactNode;
  className?: string;
}

export const Subtitle: React.FC<SubtitleProps> = ({
  children,
  className,
}) => <p className={clsx(styles["subtitle"], className)}>{children}</p>;

export interface DividerProps {
  /** Inline label, e.g. "Or". */
  label?: React.ReactNode;
  className?: string;
}

export const Divider: React.FC<DividerProps> = ({
  label,
  className,
}) => (
  <div
    className={clsx(
      styles["divider"],
      label ? styles["divider-with-label"] : styles["divider-plain"],
      className
    )}
    role="separator"
  >
    {label && <span className={styles["divider-label"]}>{label}</span>}
  </div>
);

export interface LegalFooterProps {
  /** Optional override for the leading copy. */
  prefix?: React.ReactNode;
  /** Optional className for the wrapping paragraph. */
  className?: string;
}

export const LegalFooter: React.FC<LegalFooterProps> = ({
  prefix,
  className,
}) => {
  const { t } = useTranslation("common");
  return (
    <p className={clsx(styles["legal"], className)}>
      {/* Shared-catalog legal.* values carry their own surrounding spacing
          (mobile composes the same sentence), so no JSX separators here. A
          custom `prefix` must bring its own trailing space. */}
      {prefix ?? t("legal.prefix")}
      <a
        className={styles["legal-link"]}
        href={constant.TERMS_URL}
        target="_blank"
        rel="noreferrer"
      >
        {t("legal.terms")}
      </a>
      {t("legal.conjunction")}
      <a
        className={styles["legal-link"]}
        href={constant.PRIVACY_POLICY_URL}
        target="_blank"
        rel="noreferrer"
      >
        {t("legal.privacy")}
      </a>
    </p>
  );
};
